/**
 * Resources, computed rather than ticked.
 *
 * A village stores a BASELINE and the moment it was taken. What it holds now is
 * the baseline plus whatever its buildings have produced since, capped by its
 * storage. Nothing runs on a timer: a server that ticked 2,000 villages a
 * second would burn its whole budget on players who are asleep, and a village
 * left alone for a week has to come back correct anyway — which a missed tick
 * would quietly break. (Same reason Travian does it this way.)
 *
 * THE BASELINE IS ONLY REWRITTEN WHEN SOMETHING SPENDS OR ADDS RESOURCES.
 * Rewriting it on every read looks harmless and is not: production is integer
 * arithmetic, so a read one second after the last one produces `floor(10/3600)`
 * = 0 and, if the timestamp moved anyway, that second is gone. Do that on every
 * poll and a village at 10/hour produces nothing at all, for ever.
 *
 * Both the client and the server use this file. The client predicts the numbers
 * ticking up on screen; the server decides. They agree because it is one
 * implementation, not two.
 */
import {
  FIELDS,
  FIELDS_PER_RESOURCE,
  FLAT_PRODUCTION_PER_HOUR,
  RESOURCE_KINDS,
  SERVER_SPEED_PERMILLE,
  emptyResources,
  fieldProduction,
  storageRow,
  type ResourceKind,
  type Resources,
} from "./economy.js";

const MS_PER_HOUR = 3_600_000;

/** Building levels by id. A building that has never been built is absent, not 0. */
export type BuildingLevels = Readonly<Record<string, number>>;

export interface VillageResources {
  /** What the village held at `producedAtMs`. */
  readonly resources: Resources;
  readonly producedAtMs: number;
  /** Unspent resource-milliseconds, carried across every settlement. */
  readonly remainder?: Resources;
}

export function levelOf(levels: BuildingLevels, id: string): number {
  return levels[id] ?? 0;
}

/**
 * What this village makes per hour, per resource.
 *
 * Each of the four fields on the Buildings sheet stands for the village's three
 * fields of that resource — the Fields sheet's own "village total with 3 fields"
 * column is the flat base plus three times one field, and this reproduces it.
 * Upkeep is NOT subtracted here: it belongs to the army, which this module
 * knows nothing about, so the caller passes it to `netProduction`.
 */
export function productionPerHour(levels: BuildingLevels): Resources {
  const out = emptyResources();
  for (const kind of RESOURCE_KINDS) out[kind] = FLAT_PRODUCTION_PER_HOUR;

  for (const field of FIELDS) {
    const kind = field.produces;
    if (!kind) continue;
    out[kind] += FIELDS_PER_RESOURCE * fieldProduction(levelOf(levels, field.id));
  }

  if (SERVER_SPEED_PERMILLE !== 1000) {
    for (const kind of RESOURCE_KINDS) {
      out[kind] = Math.floor((out[kind] * SERVER_SPEED_PERMILLE) / 1000);
    }
  }
  return out;
}

/**
 * Production after the army has eaten.
 *
 * Grain can go NEGATIVE — an army bigger than the farms can feed drains the
 * granary, which is the pressure that stops an attacker from sitting on a huge
 * standing army for free (02 §5). Everything else is unaffected.
 */
export function netProduction(levels: BuildingLevels, grainUpkeepPerHour = 0): Resources {
  const out = productionPerHour(levels);
  out.grain -= Math.max(0, Math.trunc(grainUpkeepPerHour));
  return out;
}

/**
 * How much of each resource this village can hold.
 *
 * The Warehouse covers wood, clay and iron; the Granary covers grain alone. A
 * village with neither still holds the level 1 amount, so a new player is not
 * capped at zero before their first build.
 */
export function capacities(levels: BuildingLevels): Resources {
  const warehouse = storageRow(levelOf(levels, "warehouse")).capacity;
  const granary = storageRow(levelOf(levels, "granary")).capacity;
  return { wood: warehouse, clay: warehouse, iron: warehouse, grain: granary };
}

/**
 * What the village holds at `nowMs`.
 *
 * Production stops at the cap rather than being thrown away later, so a full
 * warehouse is visibly full instead of silently wasting hours. Grain can fall
 * below zero when upkeep outruns the farms, and is floored at zero: the beta does not kill troops through starvation. New orders reserve grain
 * support, including queued troops.
 */
export function resourcesAt(
  village: VillageResources,
  levels: BuildingLevels,
  nowMs: number,
  grainUpkeepPerHour = 0
): Resources {
  const elapsedMs = Math.max(0, nowMs - village.producedAtMs);
  const perHour = netProduction(levels, grainUpkeepPerHour);
  const caps = capacities(levels);
  const out = emptyResources();

  for (const kind of RESOURCE_KINDS) {
    const produced = Math.floor(
      ((village.remainder?.[kind] ?? 0) + perHour[kind] * elapsedMs) / MS_PER_HOUR
    );
    const total = village.resources[kind] + produced;
    // A village already over its cap (storage was downgraded, or loot arrived)
    // keeps what it has rather than losing it here.
    const ceiling = Math.max(caps[kind], village.resources[kind]);
    out[kind] = Math.max(0, Math.min(total, ceiling));
  }
  return out;
}

/**
 * Take a fresh baseline: what it holds now, as of now.
 *
 * Call this exactly when something CHANGES the resources — a build order, loot
 * arriving, training — and never on a plain read. See the header.
 */
export function materialise(
  village: VillageResources,
  levels: BuildingLevels,
  nowMs: number,
  grainUpkeepPerHour = 0
): VillageResources {
  const resources = resourcesAt(village, levels, nowMs, grainUpkeepPerHour);
  const rates = netProduction(levels, grainUpkeepPerHour);
  const caps = capacities(levels);
  const remainder = emptyResources();
  for (const kind of RESOURCE_KINDS) {
    const numerator =
      (village.remainder?.[kind] ?? 0) + rates[kind] * Math.max(0, nowMs - village.producedAtMs);
    const ceiling = Math.max(caps[kind], village.resources[kind]);
    remainder[kind] =
      (resources[kind] >= ceiling && numerator > 0) || (resources[kind] <= 0 && numerator < 0)
        ? 0
        : ((numerator % MS_PER_HOUR) + MS_PER_HOUR) % MS_PER_HOUR;
  }
  return { resources, producedAtMs: Math.max(nowMs, village.producedAtMs), remainder };
}

export function canAfford(held: Resources, cost: Resources): boolean {
  return RESOURCE_KINDS.every((kind) => held[kind] >= cost[kind]);
}

/** What is missing, per resource, or an empty object when nothing is. */
export function shortfall(held: Resources, cost: Resources): Partial<Resources> {
  const out: Partial<Resources> = {};
  for (const kind of RESOURCE_KINDS) {
    const missing = cost[kind] - held[kind];
    if (missing > 0) out[kind] = missing;
  }
  return out;
}

/**
 * Spend, if it can be afforded.
 *
 * Returns the village with a fresh baseline and the cost taken off, or null —
 * never a partially paid village. The caller decides what to tell the player.
 */
export function spend(
  village: VillageResources,
  levels: BuildingLevels,
  cost: Resources,
  nowMs: number,
  grainUpkeepPerHour = 0
): VillageResources | null {
  const now = materialise(village, levels, nowMs, grainUpkeepPerHour);
  if (!canAfford(now.resources, cost)) return null;

  const resources = emptyResources();
  for (const kind of RESOURCE_KINDS) resources[kind] = now.resources[kind] - cost[kind];
  return { ...now, resources };
}

/**
 * How long until the village can afford this, in milliseconds.
 *
 * `null` means never: something it needs is not being produced (or is being
 * eaten faster than it is made). The UI needs this to say "in 3 h" rather than
 * greying a button out with no explanation.
 */
export function timeToAfford(
  village: VillageResources,
  levels: BuildingLevels,
  cost: Resources,
  nowMs: number,
  grainUpkeepPerHour = 0
): number | null {
  const base = materialise(village, levels, nowMs, grainUpkeepPerHour);
  const held = base.resources;
  const perHour = netProduction(levels, grainUpkeepPerHour);
  const caps = capacities(levels);

  let longest = 0;
  for (const kind of RESOURCE_KINDS) {
    const missing = cost[kind] - held[kind];
    if (missing <= 0) continue;
    // Asking for more than the village can ever hold is never, not eventually.
    if (cost[kind] > caps[kind]) return null;
    if (perHour[kind] <= 0) return null;
    longest = Math.max(
      longest,
      Math.ceil((missing * MS_PER_HOUR - (base.remainder?.[kind] ?? 0)) / perHour[kind])
    );
  }
  return longest;
}

/** Sum of a resource bundle — for "this costs 1,240 in total" and for sorting. */
export function totalOf(resources: Resources): number {
  return RESOURCE_KINDS.reduce((sum, kind) => sum + resources[kind], 0);
}

export type { ResourceKind, Resources };
